import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toggleEditMode, updateWidgetActions } from 'actions/editor';

import _ from 'lodash';
import cx from 'classnames';

import { EditorContext } from '../../index';
import { FORM, HOTSPOT } from 'constants/widgetTypes';

import { Property } from '../../components/properties/';
import FormSubmitActions from '../../components/formSubmitActions';
import { ButtonWidgetMobileAction, ButtonStyleEditor, ButtonTextEditor } from '../button';
import styles from '../button/button.module.scss';

const SubmitButtonWidget = {};

SubmitButtonWidget.moveableConfig = (editorSession, widget) => {
  const widgetElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${widget.uid}`);
  const formElement = widgetElement?.closest(`div[data-widget-type="${FORM}"]`);  
  const pageElement = widgetElement?.closest(`.editor_widget_scroll_container`);

  const rect = formElement?.getBoundingClientRect();
  const parentRect = pageElement?.getBoundingClientRect();

  const left = rect?.left - parentRect?.left;
  const top = pageElement?.scrollTop + rect?.top - parentRect?.top;

  const bounds = { 
    left: left, 
    right: left + rect?.width, 
    top: top, 
    bottom: top + rect?.height
  }

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: ['e', 'w', 's'],
    bounds: bounds,
    onReparent: (widget, parent) => {
      return false
    }
  }
}

export const useDisableFormSubmitHyperlink = (editorSession) => {
  const { id } = useParams();

  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));
  const progressive = useSelector(state => _.get(state, `gatings.gating.${id}.item.progressive`, false));
  const isAutoGatingScreen = useSelector(state => {
    const configs = _.get(state, 'storyboards.storyboard.item.gatingConfigurations', []) || [];
    return configs.some((config) => config?.autoGating && String(_.get(config, 'gating.id')) === String(id));
  });

  const isGatingEditor = entityType === 'GATING' || entityType === 'gating';
  return isGatingEditor && (Boolean(progressive) || isAutoGatingScreen);
};

const SubmitButtonWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const disableFormSubmitLink = useDisableFormSubmitHyperlink(editorSession);

  const id = _.get(props, 'uid');
  const action = _.get(props, 'actions.0');
  const storyboardPages = useSelector((state) => _.get(state, 'pages.items', []) || []);
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  useEffect(() => {
    const existingActions = _.get(props, 'actions') || [];
    if (!existingActions.length) {
      return;
    }

    const firstAction = existingActions[0];
    if (firstAction.type !== 'page') {
      return;
    }

    const rawValue = _.get(firstAction, 'value');
    const pageId =
      _.get(firstAction, 'page.id') ||
      (rawValue != null && typeof rawValue === 'object' ? rawValue.id : rawValue);

    if (pageId == null && rawValue == null) {
      dispatch(updateWidgetActions(editorSession, props.uid, []));
      return;
    }

    if (
      pageId &&
      storyboardPages.length > 0 &&
      !storyboardPages.some((page) => String(page.id) === String(pageId))
    ) {
      if (!_.get(firstAction, 'page.id')) {
        dispatch(updateWidgetActions(editorSession, props.uid, []));
      }
    }
  }, [action, storyboardPages, props.actions, editorSession, props.uid, dispatch]);

  useEffect(() => {
    if (!disableFormSubmitLink || view !== 'action') {
      return;
    }
    setView(null);
  }, [disableFormSubmitLink, view]);

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
    setView(null);
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleActionChange = (action) => {
    const actions = action ? [action] : [];
    dispatch(updateWidgetActions(editorSession, props.uid, actions));
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={handleEditMode.bind(this, 'text')}>Change Text</Property.Action>
      <Property.Divider />
      {!disableFormSubmitLink &&
        <Property.Action className={styles.iconButton} tooltip="Insert Link" onClick={() => changeView('action')}>
          <i className={cx("cleverstory-link")} />
        </Property.Action>
      }
      <Property.Action className={styles.iconButton} tooltip='Style' onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Help' onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {editMode &&
        <ButtonTextEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!disableFormSubmitLink && !editMode && view === 'action' &&
        <FormSubmitActions
          target={id}
          editorSession={editorSession}
          value={_.get(props, 'actions.0')}
          onChange={handleActionChange}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'style' &&
        <ButtonStyleEditor
          editor={editorSession}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

SubmitButtonWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <SubmitButtonWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }

      {breakpoint === 'mobile' &&
        <ButtonWidgetMobileAction editorSession={editor} breakpoint={breakpoint} {...props} showAnimation={false} />
      }
    </>
  );
}

SubmitButtonWidget.processRequest = (widget) => {
  return widget;
}

export { SubmitButtonWidget };